import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminCoLending() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", partner_type: "Bank", contact_email: "", contact_phone: "", default_split_pct: 20, status: "active", notes: "" });

  const load = () => api.get("/admin/colending/partners").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/colending/partners", { ...form, default_split_pct: Number(form.default_split_pct) });
      toast.success("Partner created");
      setOpen(false); load();
      setForm({ name: "", partner_type: "Bank", contact_email: "", contact_phone: "", default_split_pct: 20, status: "active", notes: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-colending-page">
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Co-Lending Partners</h1>
          <p className="text-sm text-slate-600 mt-1">Bank & NBFC partners for co-lending allocations.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="colending-add-btn"><Plus size={16} className="mr-1" /> Add Partner</Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Partner</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Contact</th><th className="text-left px-4 py-3">Default split</th><th className="text-left px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3">{p.partner_type}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{p.contact_email || "—"}<br/>{p.contact_phone || ""}</td>
                <td className="px-4 py-3 font-mono">{p.default_split_pct}%</td>
                <td className="px-4 py-3 capitalize"><span className="badge-approved px-2 py-0.5 rounded-full text-xs">{p.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No partners yet. Add your first co-lending partner.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="colending-dialog">
          <DialogHeader><DialogTitle>Add co-lending partner</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input data-testid="colending-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.partner_type} onValueChange={v => setForm(p => ({...p, partner_type: v}))}>
                <SelectTrigger data-testid="colending-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Bank">Bank</SelectItem><SelectItem value="NBFC">NBFC</SelectItem><SelectItem value="Fintech">Fintech</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Default split %</Label><Input type="number" data-testid="colending-split" value={form.default_split_pct} onChange={e => setForm(p => ({...p, default_split_pct: e.target.value}))} /></div>
            <div><Label>Contact email</Label><Input data-testid="colending-email" value={form.contact_email} onChange={e => setForm(p => ({...p, contact_email: e.target.value}))} /></div>
            <div><Label>Contact phone</Label><Input data-testid="colending-phone" value={form.contact_phone} onChange={e => setForm(p => ({...p, contact_phone: e.target.value}))} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="colending-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
